<!--
 ResultContainer Component
 This container component shows the results of a RocketSearch request
 using the ResultList component and SuggestList.
 devs@droxit.de
 Copyright (c) 2020 droxIT GmbH
-->

<template>
   <div class="shadow-lg" id="rocketsearch-results-container" :class="visibility">
    <div v-b-visible="mobileHandler" v-if="!showAllResults" class="d-none d-md-block" style="display: none;position: fixed;bottom: 0;"></div>
    <!-- Preview Computer-->
    <PreviewContainerComputer
        v-if="!showAllResults&&!isMobile"
        @showAll="showAllResults = true; mobileHandler(true);"
        :results="results"
        :suggests="suggests"
        :facets="facets"
        :filters="filters"
        :key="refresh_key"
    ></PreviewContainerComputer>

    <!-- All Results Computer -->
    <AllResultsComputer
        v-if="showAllResults&&!isMobile"
        @showAll="showAllResults = false"
        :search-term="searchTerm"
        :current-page="currentPage"
        :results="results"
        :hits="hits"
        :key="refresh_key"
    ></AllResultsComputer>

    <!-- Results Mobile -->
    <ResultsMobile
        v-if="isMobile"
        :search-term="searchTerm"
        :current-page="currentPage"
        :results="results"
        :suggests="suggests"
        :facets="facets"
        :filters="filters"
        :hits="hits"
        :key="refresh_key"
    ></ResultsMobile>
  </div>
</template>

<script>
  import PreviewContainerComputer from "./PreviewContainerComputer";
  import AllResultsComputer from "./AllResultsComputer";
  import ResultsMobile from "./ResultsMobile";

  /*
  * The Result Container, showing preview or all results for a search term
  */
  export default {
    name: 'ResultContainer',
    components: {
      AllResultsComputer,
      PreviewContainerComputer,
      ResultsMobile
    },
    data: function () {
      return {
        /*   Visibility variables:
             visibility:
               Visibility of the component
               @value: 'invisible', 'visible'
             showAllResults:
               If false show only a result preview, else show all results with pagination.
             isMobile:
               Boolean, false if computer and true if mobile device.
         */
        visibility: 'invisible',
        showAllResults: false,
        isMobile: false,

        /* Search Variables:
           searchTerm: String, Variable saves the newest search input.
           currentPage: Int, holds the actual page for pagination of the results.
           searchSize: holds the number of results per page.
                @value: 3, 5, 10
           suggestSize: holds the number of suggests in the preview container
                @value: 5, 10
           results: Dict, with the fields result and hits
           suggests: Array, with the suggestions.
           facets: Object with given facets to narrow the search results by predefined facets
           filters: Object with selected filters to sync with facets
        */
        searchTerm: '',
        currentPage: 1,
        searchSize: 5,
        suggestSize: 10,
        results: {},
        suggests: [],
        facets: {},
        filters: {},

        /*  Stats Variables:
            hits: amount of result items for current search
            session_id: auto-generated ID to match statistics for the same search session
            doneTypingInterval: time interval in seconds to trigger sending of stats after done typing
        */
        hits: 0,
        session_id: (Date.now().toString() + "-" + Math.random().toString().substr(2, 5)).toUpperCase(),
        doneTypingInterval: 3000,
        refresh_key: 0
      }
    },
    mounted() {
      /*
      * Triggered when size of search input is larger 2. Triggers the search and starts the timeout for stats sending.
      * @param {String} term, search input from component SearchBar.
      */
      this.$root.$on('RocketSearchEvent', (term) => {
        this.visibility = 'visible';
        this.searchTerm = term;
        this.rocketsearch(term);
        this.searchTimeout();
      });

      /*
      * Trigger when size of search inout is 2 or smaller. Hides the result container.
      */
      // this.$root.$on('RocketSearchEmptyEvent', () => {
      //   this.visibility = 'invisible';
      // });

      /* Triggered, when the user presses enter or search timeout is done.
      * sends the search input in the stats index in the engine.
      * @param {String} term, search input from component SearchBar.
      */
      this.$root.$on('StatsEvent', (term) => {
        this.searchTerm = term;
        this.sendStats();
      });

      /* Triggered when in component AllResultsMobile or AllResultsComputer the user switch to another page.
      * @param {Integer} currentPage, the page number where the user want to switch to.
      */
      this.$root.$on('TurnPage', (currentPage) => {
        this.currentPage = currentPage;
        this.rocketsearch(this.searchTerm);
      });
      this.$root.$on('StatsClickEvent', (title, url) => {
          this.sendStats(null, null, title, url);
      });

      /* Triggered when in Shop components a filter is activated.
      * @param {Array} selected_facet, user selected filters
      */
      this.$root.$on('RocketFilterSelectedEvent', (filter) => {
        if (Array.isArray(filter.options)) {
          if (filter.options.length > 0){
            this.filters[filter.name] = filter.options;
          } else {
            delete this.filters[filter.name];
          }
        } else {
          if (Object.keys(filter.options).length > 0){
            this.filters[filter.name] = filter.options;
          } else {
            delete this.filters[filter.name];
          }
        }
        this.rocketsearch(this.searchTerm);
      });

      /* Event listeners for clicking outside and inside the application
      */
      this.$root.$on('ClickOutsideEvent', () => {
          this.hide();
      });
      this.$root.$on('clickInsideEvent', () => {
          this.show();
      });
    },
    methods: {
      /*
      * Method to get the results and suggests objects for the newest search input
      * @param {String} term, search term to send to the engine to retrieve search results and suggestions from ES index.
      */
      rocketsearch: function (term) {
        let query = {"term": term};
        query = this.addFilters(query);

        let from = (this.currentPage - 1) * this.searchSize;
        fetch('api/rocketsearch.php?endpoint=search&size=' + this.searchSize+'&from=' + from, {
          method: "POST",
          body: JSON.stringify(query)
        }).then(response => response.json())
          .then((results) => {
              this.results = results;
              this.hits = results.total_hits;
              //this.refresh_key++
          }).catch(err => console.log(err));

        fetch('api/rocketsearch.php?endpoint=suggest&size=' + this.suggestSize, {
          method: "POST",
          body: JSON.stringify({"term": term})
        }).then((response) => {
          const status = response.status;
          switch (status) {
            case 204:
              this.suggests = [];
              break;
            case 200:
              response.json().then((suggests) => this.suggests = suggests);
              break;
          }
        }).catch(err => console.log(err));
      },

      /* Add filters to query */
      addFilters(query){
        var filters = [];

        // map filters lookup object into array of filters to match query syntax
        filters = Object.entries(this.filters).map( function(arr) {
          return {"name": arr[0], "options": arr[1]};
        });

        if(filters.length){
            query['filters'] = filters;
        }

        if(window.__env.config.ADD_AGGREGATIONS){
          query["facets"] = true;
        }

        return query
      },

      /* Reset the Timer. To variate the timeout interval, set the component variable doneTypingInterval.*/
      searchTimeout: function () {
          if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
          }
          this.timer = setTimeout(this.sendStats, this.doneTypingInterval);
      },
      /* Sends the lates searchTerm to the engine to enrich the stats index.
      * Called from the events 'beforeunload', 'StatsEvent' and timer timeout event.
      */
      sendStats: function (search_lang=null, search_url=null, clicked_title=null, clicked_url=null, timestamp=null) {
        // Reset timer to prevent redundant triggering of sendStats through timeout event
        if (this.timer) {
          clearTimeout(this.timer);
          this.timer = null;
        }
        if (this.searchTerm) {
          var doc = {};
          doc['term'] = this.searchTerm;
          doc['hits'] = this.hits ? this.hits : 0;
          doc['session_id'] = this.session_id;
          doc['source'] = 'integration';
          if (search_lang) {
            doc['search_lang'] = search_lang;
          }
          else {
            doc['search_lang'] = navigator.language ? navigator.language : null;
          }
          if (search_url) {
            doc['search_url'] = search_url;
          }
          else {
            doc['search_url'] = window.location.href;
          }
          if (clicked_title) {
            doc['clicked_title'] = clicked_title;
          }
          if (clicked_url) {
            doc['clicked_url'] = clicked_url;
          }
          if (timestamp) {
            doc['timestamp'] = timestamp;
          }
          fetch('api/rocketsearch.php?endpoint=stats', {
            method: "POST",
            body: JSON.stringify(doc)
          }).catch(err => console.log(err));
        }
      },
      /*
      * Event listener that is called every time the <div> with the class class="d-none d-md-block" is loaded.
      * The classes check if the window is pc or medium device size or not and toggle the "notMobile" boolean.
      * @param {Boolean} notMobile, true if window is medium device or larger, else false.
      */
      mobileHandler(notMobile) {
        this.currentPage = 1;
        if (notMobile) {
          this.searchSize = this.showAllResults ? window.__env.config.NUM_ALL_RESULTS : window.__env.config.NUM_PREVIEW_RESULTS;
          this.suggestSize = 10;
          this.rocketsearch(this.searchTerm);
          this.isMobile = false;
        } else {
          this.searchSize = window.__env.config.NUM_RESULTS_MOBILE;
          this.suggestSize = 3;
          this.rocketsearch(this.searchTerm);
          this.isMobile = true;
        }
      },
      /*
      * Show / Hide Result Container on click inside search / outside the application.
      */
      hide(){
        this.visibility = "invisible";
      },
      show(){
        this.visibility = "visible";
      },
    }
  }
</script>
